import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable, ReplaySubject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ApiClientService } from "~app/common/services/api-client.service";
import { Utils } from "~app/common/services/utils";
import { IProduktdefinition, IProduktinstruktion, IProduktinstruktionHistorik } from "../models/IProduktdefinition";
import { IProduktinstruktionSearchResult } from "../models/IProduktinstruktionSearchResult";
import { IValbaraHandelssortiment } from "../models/IValbaraHandelssortiment";
import { MassediteraProduktdefinitionDto } from "../models/MassediteraProduktdefinitionDto";
import { MassediteraProduktinstruktionerResponseDto } from "../models/MassediteraProduktinstruktionerResponseDto";
import { IProduktdefinitionBasdata, IProduktinstruktionSearchModel } from "../models/ProduktdefinitionModels";
import { ValidationMessage } from "../models/ValidationMessage";

@Injectable({
    providedIn: "root"
})
export class ProduktinstruktionService {
    alerts$ = new BehaviorSubject<IMessageModel[]>([]);
    searchResult$: ReplaySubject<IProduktinstruktionSearchResult[]>;

    private readonly apiClientService = inject(ApiClientService);

    constructor() {
        this.searchResult$ = new ReplaySubject<IProduktinstruktionSearchResult[]>();
    }

    aktiveraProdktinstruktion(puid: string): Observable<IProduktinstruktion> {
        return this.apiClientService.post(`aptering/Produktinstruktion/${puid}/activate`, false);
    }

    checkoutProduktdefinition(puid: string, historiskVersionId?: number): Observable<IProduktdefinition> {
        if (historiskVersionId) {
            return this.apiClientService.post(`Aptering/Produktinstruktion/${puid}/Produktdefinition/${historiskVersionId}/checkout`, false);
        }
        return this.apiClientService.post(`Aptering/Produktinstruktion/${puid}/Produktdefinition/checkout`, false);
    }

    createProduktinstruktion(data: IProduktdefinition): Observable<IProduktdefinition> {
        return this.apiClientService.post(`Aptering/Produktinstruktion/create`, data, false);
    }

    createValidationmessages(messages: ValidationMessage[]): IMessageModel[] {
        return messages.map(x => ({
            severity: this.mapSeverity(x),
            message: x.message,
            anchor: undefined,
            anchorText: undefined,
            code: undefined,
            metadata: undefined,
            validationScope: x.scope
        }));
    }

    deleteProduktinstruktion(puid: string): Observable<any> {
        return this.apiClientService.delete(`Aptering/Produktinstruktion/${puid}`, false);
    }

    discardProduktdefinition(puid: string): Observable<void> {
        return this.apiClientService.delete(`Aptering/Produktinstruktion/${puid}/Produktdefinition/utkast/discard`, false);
    }

    download(puid: string[], viol3: boolean, format: "combinedpin" | "env" | "singlepin", filnamnsprefix: string): Observable<any> {
        return new Observable(observer => {
            this.apiClientService.postRaw(`Aptering/Produktinstruktion/download`, {
                puid: puid,
                viol3: viol3,
                format: format
            }, false, { responseType: "text" })
            // eslint-disable-next-line sonarjs/deprecation
                .subscribe((data) => {
                    const viol3Ext = viol3 ? "V3" : "";
                    const fileExtension = format === "env" ? "env" : "pin";
                    Utils.download(data, `${viol3Ext}${filnamnsprefix}.${fileExtension}`);
                    observer.next(true);
                    observer.complete();
                }, error => {
                    observer.next(false);
                    observer.complete();
                });
        });
    }

    downloadPin(puid: string, tradslagsgruppNamn: string, namn: string, viol3: boolean, utkast: boolean): void {
        this.forhandsgranskaPin(puid, viol3, utkast).subscribe((data) => {
            const ext = utkast ? "-utkast" : "";
            const viol3Ext = viol3 ? "V3" : "";

            if (data) {
                Utils.download(data, `${viol3Ext}${tradslagsgruppNamn}_${namn}_${puid}${ext}.pin`);
            }
        });
    }

    downloadPinHistorisk(puid: string, tradslagsgruppNamn: string, namn: string, produktdefinitionId: number, viol3: boolean): void {
        this.forhandsgranskaPinHistorik(puid, produktdefinitionId, viol3).subscribe((data) => {
            const viol3Ext = viol3 ? "V3" : "";
            if (data) {
                Utils.download(data, `${viol3Ext}${tradslagsgruppNamn}_${namn}_${puid}-historisk.pin`);
            }
        });
    }

    forhandsgranskaPin(puid: string, viol3: boolean, utkast: boolean): Observable<any> {
        const utkastUrl = utkast ? "utkast/" : "";
        return this.apiClientService.getRaw(`Aptering/Produktinstruktion/${puid}/Produktdefinition/${utkastUrl}forhandsgranskaPin?viol3=${viol3}`, false, { responseType: "text" });
    }

    forhandsgranskaPinHistorik(puid: string, id: number, viol3: boolean): Observable<any> {
        return this.apiClientService.getRaw(
            `Aptering/Produktinstruktion/${puid}/Produktdefinition/forhandsgranskaPin/historik/${id}?viol3=${viol3}`,
            false,
            { responseType: "text" });
    }

    getHandelssortiment(): Observable<IValbaraHandelssortiment[]> {
        return this.apiClientService.get<IValbaraHandelssortiment[]>(`handelssortiment`, false, { aktiv: true,
            hasViolId: true });
    }

    getHistorik(puid: string) {
        return this.apiClientService.get<IProduktinstruktionHistorik>(`Aptering/Produktinstruktion/${puid}/historik`, false);
    }

    getHistoriskProduktinstruktionDetails(puid: string, id: number): Observable<IProduktdefinition> {
        return this.apiClientService.get<IProduktdefinition>(`Aptering/Produktinstruktion/${puid}/produktdefinition/historik/${id}`, false);
    }

    getProduktinstruktionBasData(): Observable<IProduktdefinitionBasdata> {
        return this.apiClientService.get<IProduktdefinitionBasdata>(`Aptering/Produktinstruktion/basdata`, false);
    }

    getProduktinstruktionDetails(puid: string): Observable<IProduktinstruktion> {
        return this.apiClientService.get<IProduktinstruktion>(`Aptering/Produktinstruktion/${puid}`, false);
    }

    getProduktinstruktionDetailsList(puids: string[]): Observable<MassediteraProduktinstruktionerResponseDto> {
        return this.apiClientService.get<MassediteraProduktinstruktionerResponseDto>(`Aptering/Produktinstruktion/GetProduktinstruktionerList?puids=${puids.join(",")}`, false);
    }

    inaktiveraProdktinstruktion(puid: string): Observable<IProduktinstruktion> {
        return this.apiClientService.post(`aptering/Produktinstruktion/${puid}/deactivate`, false);
    }

    kopieraProduktinstruktion(puid: string): Observable<IProduktinstruktion> {
        return this.apiClientService.post(`Aptering/Produktinstruktion/${puid}/copy`, false);
    }

    mapSeverity(validationMessage: ValidationMessage): MessageSeverity {
        switch (validationMessage.severity) {
            case "Warning": {
                return MessageSeverity.Warning;
            }
            case "Info": {
                return MessageSeverity.Info;
            }
            case "Error": {
                return MessageSeverity.Error;
            }
            default: {
                return null;
            }
        }
    }

    publiceraHistoriskProduktdefinition(puid: string, id: number): Observable<IProduktdefinition> {
        return this.apiClientService.post(`Aptering/Produktinstruktion/${puid}/Produktdefinition/historik/${id}/checkin`, false);
    }

    publiceraProduktdefinition(puid: string): Observable<IProduktdefinition> {
        return this.apiClientService.post(`Aptering/Produktinstruktion/${puid}/Produktdefinition/utkast/checkin`, false);
    }

    publishMasseditering(data: string[]): Observable<any> {
        return this.apiClientService.put(`Aptering/Produktinstruktion/Produktdefinition/publishMasseditering`, data, false);
    }

    saveMassediteringProduktinstruktion(data: MassediteraProduktdefinitionDto): Observable<MassediteraProduktdefinitionDto> {
        return this.apiClientService.put(`Aptering/Produktinstruktion/Produktdefinition/SaveMasseditering`, data, false);
    }

    saveProduktinstruktion(data: IProduktdefinition): Observable<IProduktdefinition> {
        return this.apiClientService.put(`Aptering/Produktinstruktion/${data.puid}/Produktdefinition/save`, data, false);
    }

    search(produktinstruktionSearchParams: IProduktinstruktionSearchModel): Observable<IProduktinstruktionSearchResult[]> {
        return this.apiClientService.post<IProduktinstruktionSearchModel, IProduktinstruktionSearchResult[]>("aptering/Produktinstruktion",
            produktinstruktionSearchParams,
            false)
            .pipe(tap(x => {
                this.searchResult$.next(x);
            }));
    }

    setValidationmessage(messages: ValidationMessage[]) {
        this.alerts$.next(this.createValidationmessages(messages));
    }

    validateMasseditProduktinstruktioner(puids: string[]): Observable<IProduktinstruktion[]> {
        return this.apiClientService.get<IProduktinstruktion[]>(`Aptering/Produktinstruktion/ValidateMasseditProduktinstruktioner?puids=${puids.join(",")}`, false);
    }

    validateProduktinstruktion(puid: string): Observable<ValidationMessage[]> {
        return this.apiClientService.get<ValidationMessage[]>(`Aptering/Produktinstruktion/${puid}/Produktdefinition/utkast/validate?shouldThrow=false`, false);
    }

    validatePublishMasseditering(puids: string[]): Observable<ValidationMessage[]> {
        const observables: Observable<{ puid: string; messages: ValidationMessage[] }>[] = [];
        const execute = (puid: string): Observable<{ puid: string; messages: ValidationMessage[] }> =>
            this.apiClientService.get<ValidationMessage[]>(`Aptering/Produktinstruktion/${puid}/Produktdefinition/utkast/validate?shouldThrow=false`, false)
                .pipe(map(x => ({
                    puid: puid,
                    messages: x
                })));

        puids.forEach(puid => {
            observables.push(execute(puid));
        });

        const validationMessages: ValidationMessage[] = [];
        return forkJoin(observables).pipe(map(x => {
            x.forEach(puid => {
                validationMessages.push(...puid.messages.map(y => ({
                    message: `${puid.puid}: ${y.message}`,
                    severity: y.severity,
                    scope: y.scope
                })));
            });
            return validationMessages;
        }));
    }
}
